import React from "react";
import { connect } from "react-redux";
import language from "../../../language";
import { withRouter } from "../../../utils/withRouter";
import { DashboardHeader } from "../../../components/Header";
import Patients from "../../../components/Patient/patients";
import { Tabs } from "../../../components/Tabs";
import PatientLabResult from "../../../components/Patient/lab-results";
import PatientHospitalizations from "../../../components/Patient/hospitalizations";
import { getStorage } from "../../../utils/storage";
import { defineRole } from "../../../utils/handleAccessRoles";
import { ROLE_PATIENT } from "../../../constants/ROLES";

class PatientScreen extends React.Component {
  state = {
    user: {},
    showGuide: false,
  };

  componentDidMount = async () => {
    const user = await getStorage();
    this.setState({ user });
  };

  render() {
    const canReadHospitalization =
      defineRole({
        roles: this.state?.user?.accessRole,
        menu: ROLE_PATIENT,
        operation: "read",
      });

    const { showGuide } = this.state;

    return (
      <div>
        <div className="card-dashboard-content">
          <div className="card-body">
            <DashboardHeader
              title={language[this.props.defaultLanguage].patients}
            />
            <Tabs
              options={[
                {
                  title: language[this.props.defaultLanguage].patients,
                  data: <Patients />,
                },
                canReadHospitalization && {
                  title:
                    language[this.props.defaultLanguage]
                      .hospitalization_records,
                  data: <PatientHospitalizations />,
                },
                {
                  title: language[this.props.defaultLanguage].known_lab_results,
                  data: <PatientLabResult />,
                },
              ].filter(Boolean)}
            />
          </div>
        </div>

        {/* ── Help guide ── */}
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1050 }}>
          {showGuide && (
            <div style={{
              position: "absolute",
              bottom: 54,
              right: 0,
              width: "min(400px, calc(100vw - 32px))",
              maxHeight: "75vh",
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
              border: "1px solid #dee2e6",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}>
              {/* Header */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 16px", borderBottom: "1px solid #dee2e6",
                background: "#f8f9fa", borderRadius: "10px 10px 0 0", flexShrink: 0,
              }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>How to Use the Patients Module</span>
                <button
                  onClick={() => this.setState({ showGuide: false })}
                  style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#888", lineHeight: 1, padding: 0 }}
                >×</button>
              </div>

              {/* Scrollable body */}
              <div style={{ overflowY: "auto", padding: "16px", fontSize: 13, lineHeight: 1.65 }}>

                {/* Overview */}
                <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Overview</p>
                <p style={{ marginBottom: 16 }}>
                  The Patients module lets you register patients, track their <strong>hospitalization records</strong>, and view their <strong>known lab results</strong>. Use the tabs at the top to switch between these sections.
                </p>

                {/* Adding a patient */}
                <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Adding a New Patient</p>
                <ol style={{ paddingLeft: 18, marginBottom: 16 }}>
                  <li>Click <strong>+ Add</strong> at the top right.</li>
                  <li>Select the <strong>Identity Document Type</strong> (National ID, Passport, or No Document).</li>
                  <li>Enter the <strong>Patient ID</strong> — this is the ID from the hospital's system (e.g. HIS number).</li>
                  <li>Fill in <strong>First Name</strong>, <strong>Last Name</strong>, <strong>Date of Birth</strong>, and <strong>Gender</strong> (all required).</li>
                  <li>Add optional fields: Contact, Nationality, Blood Group, Father/Mother Name, and Address.</li>
                  <li>Click <strong>Submit</strong> — the system will generate a unique <strong>Patient BBMS Number</strong> automatically.</li>
                </ol>

                {/* Identity document types */}
                <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Identity Document Types</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                  {[
                    { label: "National ID", desc: "16-digit Rwandan national identity number." },
                    { label: "Passport", desc: "Passport number for foreign nationals." },
                    { label: "No Document", desc: "Use when the patient has no identity document (e.g. newborns, emergency cases)." },
                    { label: "Other", desc: "Any other official identity document." },
                  ].map(({ label, desc }) => (
                    <div key={label} style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 10px" }}>
                      <strong style={{ display: "block", marginBottom: 3, fontSize: 12 }}>{label}</strong>
                      <span style={{ fontSize: 11, color: "#555" }}>{desc}</span>
                    </div>
                  ))}
                </div>

                {/* Tabs */}
                <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Module Tabs</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                  {[
                    { label: "Patients", desc: "Register and manage patient profiles." },
                    { label: "Hospitalization Records", desc: "Track each patient's hospital admissions and blood transfusion requests." },
                    { label: "Known Lab Results", desc: "View pre-existing lab results linked to a patient (e.g. blood group, antibodies)." },
                  ].map(({ label, desc }) => (
                    <div key={label} style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 10px" }}>
                      <strong style={{ display: "block", marginBottom: 3, fontSize: 12 }}>{label}</strong>
                      <span style={{ fontSize: 11, color: "#555" }}>{desc}</span>
                    </div>
                  ))}
                </div>

                {/* Tips */}
                <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Tips</p>
                <ul style={{ paddingLeft: 18, marginBottom: 16 }}>
                  <li>Use the <strong>search bar</strong> to find a patient by name, Patient ID, contact, or BBMS number.</li>
                  <li>A patient's <strong>Date of Birth</strong> cannot be set to a future date.</li>
                  <li>If a patient already exists with the same identity number or contact, the system will block a duplicate.</li>
                </ul>

                {/* Warning */}
                <div style={{ background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 6, padding: "8px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 15, lineHeight: 1.4 }}>⚠</span>
                  <span style={{ fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>
                    <strong>Note:</strong> Patient data is used directly in blood request workflows. Ensure the name, date of birth, and blood group are accurate before submitting a blood request for that patient.
                  </span>
                </div>

              </div>
            </div>
          )}

          {/* Floating "?" button */}
          <button
            title="How to use the Patients module"
            onClick={() => this.setState(prev => ({ showGuide: !prev.showGuide }))}
            style={{
              width: 44, height: 44, borderRadius: "50%",
              backgroundColor: showGuide ? "#fff" : "var(--primary-color)",
              color: showGuide ? "var(--primary-color)" : "#fff",
              border: "2px solid var(--primary-color)",
              boxShadow: "0 3px 10px rgba(0,0,0,0.28)",
              cursor: "pointer", fontSize: 22, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              lineHeight: 1,
            }}
          >?</button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(withRouter(PatientScreen));
